import React, { useCallback, useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { BusinessOutlined, CalendarMonthOutlined, CheckRounded, ErrorOutlineOutlined, FileUploadOutlined, ListAltOutlined, RemoveRounded } from "@mui/icons-material";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { showToast } from "services/utils/Status";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { esiEligibilityHeader } from "pages/PayRoll/PayRollHeaders";
import { ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid, StatutoryList, StatutoryPage, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { fetchEmployerCodes } from "pages/PayRoll/Configuration/StatutoryConfiguration/ESI/esiConfigApi";
import { fetchEligibility, fetchEligibilityPeriods, fetchEsiEmployee, saveEsiEmployee } from "./esiEligibilityApi";
import { EsiDetailsPanel, EsiEditPanel } from "./EsiEmployeePanels";
import { EsiIpImport } from "./EsiIpImport";
import { OpenSettingsButton } from "pages/PayRoll/Shared/settingsSwitch";

// Payroll > Statutory > ESI > Eligibility (screens E1–E4): who is covered
// by ESI for a contribution period, until when, and their IP number. The
// server decides coverage (ESI API, esiEligibilityApi.js); search, cards
// and paging go to the server. Monthly ESI amounts are in Reports.

const ALL = "all";
const PAGE_SIZE = 50;
const EXPORT_PAGE_SIZE = 200;
const SEARCH_DELAY = 400;
const EMPLOYER_CODES_ROUTE = "/admin/payroll/configuration/statutory-configuration?item=esi&tab=employer-codes";
const COVERAGE_TONES = { Covered: "green", "Covered · continuing": "green", "Covered · disability": "green", "Covered · override": "purple" };
const STALE_MESSAGE = "This employee's ESI details were changed by someone else. They have been reloaded — check them and try again.";

export const StatutoryESI = () => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh data per company.
  return <EsiEligibilityScreen key={`${domain}:${topbarCompanyId}`} domain={domain} companyId={topbarCompanyId} />;
};

// Filter row matches Team Workspace reports: one card, 300px filters
// side by side from md up (long labels end in ...), full width below md.
const filterRowSx = {
  display: "grid",
  gap: 1.5,
  alignItems: "center",
  width: "100%",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 300px))" },
  justifyContent: { md: "start" },
};
const filterButtonSx = { width: "100%", minWidth: 0 };

const EsiEligibilityScreen = ({ domain, companyId }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const history = useHistory();
  const [periods, setPeriods] = useState(null);
  const [periodValue, setPeriodValue] = useState("");
  const [codes, setCodes] = useState({ list: [], unlinked: [] });
  const [codeFilter, setCodeFilter] = useState(ALL);
  const [card, setCard] = useState(ALL);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState({ loading: true, error: "", rows: [], count: 0, cards: {}, configured: true, period: null });
  const [viewId, setViewId] = useState(null);
  const [details, setDetails] = useState({ loading: false, error: "", detail: null });
  const [detailsKey, setDetailsKey] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const firstSearch = useRef(true);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "esi_eligibility_saved_views",
    fullHeaders: esiEligibilityHeader,
  });

  // Periods (current one by default) and the employer codes for the filter
  // and the "location not linked" banner.
  useEffect(() => {
    const controller = new AbortController();
    fetchEligibilityPeriods({ domain, companyId, signal: controller.signal })
      .then((list) => {
        setPeriods(list);
        setPeriodValue((current) => current || (list.find((period) => period.isCurrent) || list[list.length - 1])?.value || "");
      })
      .catch((error) => {
        if (!controller.signal.aborted) setPeriods([]);
        if (!controller.signal.aborted) showToast("error", error.message || "Could not load the ESI periods.");
      });
    fetchEmployerCodes({ domain, companyId, signal: controller.signal })
      .then((data) => setCodes({ list: data.codes.filter((code) => code.status === "Active"), unlinked: data.unlinked }))
      .catch(() => {});
    return () => controller.abort();
  }, [domain, companyId]);

  // Search waits for typing to pause, then starts again from page 1.
  useEffect(() => {
    if (firstSearch.current) {
      firstSearch.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(0);
    }, SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [search]);

  const codeId = codeFilter === ALL ? undefined : codeFilter;
  useEffect(() => {
    if (periods == null) return undefined;
    const controller = new AbortController();
    setList((current) => ({ ...current, loading: true, error: "" }));
    fetchEligibility({ domain, companyId, period: periodValue || undefined, codeId, card, search: query, page: page + 1, pageSize, signal: controller.signal })
      .then((data) => setList({ loading: false, error: "", ...data }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setList((current) => ({ ...current, loading: false, error: error.message || "Could not load ESI eligibility." }));
      });
    return () => controller.abort();
  }, [domain, companyId, periods, periodValue, codeId, card, query, page, pageSize, reloadKey]);

  useEffect(() => {
    if (viewId == null) {
      setDetails({ loading: false, error: "", detail: null });
      return undefined;
    }
    const controller = new AbortController();
    setDetails((current) => ({ loading: true, error: "", detail: current.detail?.row.id === viewId ? current.detail : null }));
    fetchEsiEmployee({ domain, companyId, employeeId: viewId, period: periodValue || undefined, signal: controller.signal })
      .then((detail) => setDetails({ loading: false, error: "", detail }))
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ loading: false, error: error.message || "Could not load the ESI details.", detail: null });
      });
    return () => controller.abort();
  }, [domain, companyId, viewId, periodValue, detailsKey]);

  const reloadList = () => setReloadKey((key) => key + 1);
  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(0);
  };

  // Export: every matching row, page by page.
  const exportRows = useCallback(async () => {
    const all = [];
    for (let next = 1; ; next += 1) {
      // eslint-disable-next-line no-await-in-loop
      const data = await fetchEligibility({ domain, companyId, period: periodValue || undefined, codeId, card, search: query, page: next, pageSize: EXPORT_PAGE_SIZE });
      all.push(...data.rows);
      if (!data.rows.length || all.length >= data.count) return all;
    }
  }, [domain, companyId, periodValue, codeId, card, query]);

  const saveDetails = async (form) => {
    const detail = details.detail;
    try {
      const result = await saveEsiEmployee({
        domain,
        companyId,
        employeeId: detail.row.id,
        form,
        rowVersion: detail.row_version,
        disabilityVisible: detail.identifiers.disability_visible,
      });
      setDetails({ loading: false, error: "", detail: result.detail });
      setEditOpen(false);
      reloadList();
      showToast("success", result.message || `${detail.row.name}'s ESI details saved.`);
    } catch (error) {
      if (error?.code === "STALE_VERSION") {
        showToast("error", STALE_MESSAGE);
        setDetailsKey((key) => key + 1);
        reloadList();
        return undefined;
      }
      throw error;
    }
    return undefined;
  };

  const period = (periods || []).find((item) => item.value === periodValue);
  const periodLabel = list.period?.label || period?.label || "";
  const cardValue = (key) => Number(list.cards?.[key]) || 0;
  const cards = [
    { key: ALL, title: "All", value: cardValue("all"), icon: ListAltOutlined, tone: "blue" },
    { key: "covered", title: "Covered", value: cardValue("covered"), icon: CheckRounded, tone: "green" },
    { key: "not_covered", title: "Not covered", value: cardValue("not_covered"), icon: RemoveRounded, tone: "amber" },
    { key: "ip_missing", title: "IP number missing / due", value: cardValue("ip_missing"), icon: ErrorOutlineOutlined, tone: "red" },
  ];
  const periodOptions = (periods || []).map((item) => ({ value: item.value, label: item.label }));
  const codeOptions = [
    { value: ALL, label: "All codes" },
    ...codes.list.map((code) => ({ value: String(code.id), label: `${code.branch_name} · ${code.code_display || code.code}` })),
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
              {initialsOf(row.name)}
            </Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "code_label":
        return row.code ? row.code_label || row.code.code_display : <Mui.Box component="span" sx={{ color: "#DC2626" }}>{row.site ? "Site not linked" : "No site"}</Mui.Box>;
      case "wage_at_test":
        return row.wage_at_test === "" ? "—" : Number(row.wage_at_test).toLocaleString("en-IN");
      case "coverage_label":
        return <ToneChip label={row.coverage_label} tone={COVERAGE_TONES[row.coverage_label] || (row.covered ? "green" : "slate")} />;
      case "ip_status":
        return row.ip.tone ? <ToneChip label={row.ip.label} tone={row.ip.tone} /> : "—";
      default:
        return row[key] || "—";
    }
  };

  return (
    <StatutoryPage
      breadcrumb={["Payroll", "Statutory", "ESI", "Eligibility"]}
      title="ESI Eligibility"
      switchAction={<OpenSettingsButton group="statutory" item="esi" />}
      subtitle="Who is covered by ESI this contribution period. Payroll calculates ESI only for covered employees. Rules are in Payroll › Payroll Settings › Statutory › ESI."
    >
      <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
        <Mui.Box sx={filterRowSx}>
          <CompactHeaderSelectFilter
            dataList={periodOptions}
            selectedValue={periodOptions.find((option) => option.value === periodValue) || (periods ? "None" : "Loading…")}
            updateDataFunction={(event, option) => {
              if (!option?.value) return;
              resetPage(setPeriodValue)(option.value);
              setViewId(null);
            }}
            Icons={CalendarMonthOutlined}
            staticLabel="Period"
            widthSx={filterButtonSx}
          />
          <CompactHeaderSelectFilter
            dataList={codeOptions}
            selectedValue={codeOptions.find((option) => option.value === codeFilter) || codeOptions[0]}
            updateDataFunction={(event, option) => option?.value && resetPage(setCodeFilter)(option.value)}
            Icons={BusinessOutlined}
            staticLabel="ESI Code"
            widthSx={filterButtonSx}
          />
        </Mui.Box>
      </Mui.Box>

      <StatCardGrid cards={cards} value={card} onChange={resetPage(setCard)} />

      <Mui.Stack spacing={1.5} sx={{ mb: 1.5 }}>
        {list.error ? (
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load ESI eligibility."
            body={list.error}
            action={<SecondaryButton onClick={reloadList} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        ) : null}
        {!list.loading && !list.error && !list.configured ? (
          <RequestInfoBanner
            tone="warning"
            title="ESI is not configured for this period."
            body="Set up the ESI version, contribution periods and employer codes in Payroll › Payroll Settings › Statutory › ESI."
          />
        ) : null}
        {codes.unlinked.length ? (
          <RequestInfoBanner
            tone="danger"
            body={`Site ${codes.unlinked.map((site) => (site.employees ? `${site.label} (${site.employees} employees)` : site.label)).join(", ")} ${
              codes.unlinked.length === 1 ? "has" : "have"
            } no ESI code. Coverage is unchanged, but those employees can't be in an ESIC file until the site is added to an ESI code.`}
            action={
              <SecondaryButton onClick={() => history?.push(EMPLOYER_CODES_ROUTE)} sx={{ whiteSpace: "nowrap" }}>
                Open Employer Codes
              </SecondaryButton>
            }
          />
        ) : null}
      </Mui.Stack>

      <StatutoryList
        title={`ESI Eligibility ${periodLabel}`.trim()}
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={list.rows}
        loading={list.loading}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="employees"
        searchPlaceholder="Search name, ID or IP number"
        metadata={{ "Company Name": topbarCompanyName || "N/A", Period: periodLabel || "–", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
        server={{
          search,
          onSearchChange: setSearch,
          page,
          rowsPerPage: pageSize,
          count: list.count,
          onPageChange: setPage,
          onRowsPerPageChange: (size) => {
            setPageSize(size);
            setPage(0);
          },
          exportRows,
        }}
        action={
          <SecondaryButton startIcon={<FileUploadOutlined />} onClick={() => setImportOpen(true)} sx={{ minHeight: 40, whiteSpace: "nowrap", flexShrink: 0 }}>
            Import IP numbers
          </SecondaryButton>
        }
      />
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="No monthly amounts here — monthly ESI is in Reports › Statutory Reports › ESI." />
      </Mui.Box>

      <EsiDetailsPanel
        open={viewId != null && !editOpen}
        loading={details.loading && !details.detail}
        error={details.error}
        detail={details.detail}
        onRetry={() => setDetailsKey((key) => key + 1)}
        onClose={() => setViewId(null)}
        onEdit={() => setEditOpen(true)}
      />
      <EsiEditPanel open={editOpen && Boolean(details.detail)} detail={details.detail} onClose={() => setEditOpen(false)} onSave={saveDetails} />
      <EsiIpImport
        open={importOpen}
        domain={domain}
        companyId={companyId}
        onClose={() => setImportOpen(false)}
        onImported={() => {
          reloadList();
          if (viewId != null) setDetailsKey((key) => key + 1);
        }}
      />
    </StatutoryPage>
  );
};
